import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, Phone, Mail, MessageCircle } from "lucide-react";
import { NAV_LINKS } from "../lib/site";
import { useContent } from "../lib/content";
import { useQuote } from "./QuoteModal";

export const Header = () => {
  const { openQuote } = useQuote();
  const { SITE } = useContent();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      {/* Top bar */}
      <div className="bg-brand-navy text-white" data-testid="top-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-xs sm:px-6">
          <div className="flex items-center gap-4">
            <a href={SITE.phone1} className="flex items-center gap-1.5 hover:text-brand-orange transition-colors" data-testid="topbar-call-1">
              <Phone className="h-3 w-3" /> {SITE.phone1Display}
            </a>
            <a href={SITE.phone2} className="hidden sm:flex items-center gap-1.5 hover:text-brand-orange transition-colors" data-testid="topbar-call-2">
              <Phone className="h-3 w-3" /> {SITE.phone2Display}
            </a>
            <a href={`mailto:${SITE.emails[0]}`} className="hidden md:flex items-center gap-1.5 hover:text-brand-orange transition-colors" data-testid="topbar-email">
              <Mail className="h-3 w-3" /> {SITE.emails[0]}
            </a>
          </div>
          <Link to="/pm-surya-ghar" className="flex items-center gap-1.5 font-semibold text-brand-orange hover:text-amber-400 transition-colors" data-testid="topbar-pm-surya">
            PM Surya Ghar: Muft Bijli Yojana
          </Link>
        </div>
      </div>

      {/* Main nav */}
      <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/90 shadow-sm backdrop-blur-md" data-testid="main-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="header-logo">
            <img src="/assets/logo-horizontal.webp" alt="DHIYAA SOLAR ENERGY logo" className="h-14 w-auto sm:h-16 lg:h-[76px]" />
          </Link>

          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label} className="group relative">
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 font-heading text-[13px] font-semibold transition-colors ${
                        isActive || location.pathname.startsWith("/solutions")
                          ? "text-brand-orange"
                          : "text-brand-navy hover:text-brand-blue"
                      }`
                    }
                    data-testid={link.testid}
                  >
                    {link.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </NavLink>
                  <div className="invisible absolute left-0 top-full z-50 w-60 translate-y-2 rounded-xl border border-slate-100 bg-white p-2 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {link.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="block rounded-lg px-3.5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-brand-sky hover:text-brand-blue"
                        data-testid={`dropdown-${c.to.split("/").pop()}`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.label}
                  to={link.to}
                  className={({ isActive }) =>
                    `whitespace-nowrap rounded-full px-3 py-2 font-heading text-[13px] font-semibold transition-colors ${
                      isActive ? "text-brand-orange" : "text-brand-navy hover:text-brand-blue"
                    }`
                  }
                  data-testid={link.testid}
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2.5">
            <a
              href={SITE.phone1}
              className="hidden lg:flex items-center gap-2 whitespace-nowrap rounded-full border border-brand-blue/20 px-4 py-2.5 font-heading text-[13px] font-bold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white"
              data-testid="header-call-btn"
            >
              <Phone className="h-3.5 w-3.5" /> Call Now
            </a>
            <button
              onClick={() => openQuote()}
              className="hidden sm:flex items-center whitespace-nowrap rounded-full bg-brand-orange px-5 py-2.5 font-heading text-[13px] font-bold uppercase tracking-wide text-white transition-all hover:bg-brand-orangedark hover:shadow-lg hover:shadow-brand-orange/30"
              data-testid="header-quote-btn"
            >
              Get Free Quote
            </button>
            <button
              className="rounded-lg p-2 text-brand-navy xl:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div className="fixed inset-0 z-[90] xl:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-brand-navy/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col bg-white shadow-2xl"
              data-lenis-prevent
              data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between border-b border-slate-100 p-4">
                <img src="/assets/logo-horizontal.webp" alt="DHIYAA SOLAR ENERGY" className="h-12 w-auto" />
                <button onClick={() => setMobileOpen(false)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100" aria-label="Close menu" data-testid="mobile-menu-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto p-4" data-testid="mobile-nav">
                {NAV_LINKS.map((link) =>
                  link.children ? (
                    <div key={link.label} className="border-b border-slate-50">
                      <button
                        className="flex w-full items-center justify-between py-3.5 font-heading text-sm font-bold text-brand-navy"
                        onClick={() => setSolOpen((s) => !s)}
                        data-testid="mobile-nav-solutions-toggle"
                      >
                        {link.label}
                        <ChevronDown className={`h-4 w-4 transition-transform ${solOpen ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {solOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                            {link.children.map((c) => (
                              <Link
                                key={c.to}
                                to={c.to}
                                onClick={() => setMobileOpen(false)}
                                className="block rounded-lg px-3 py-2.5 text-sm text-slate-600 hover:bg-brand-sky"
                                data-testid={`mobile-${c.to.split("/").pop()}`}
                              >
                                {c.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className="block border-b border-slate-50 py-3.5 font-heading text-sm font-bold text-brand-navy"
                      data-testid={`mobile-${link.testid}`}
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </nav>
              <div className="space-y-2.5 border-t border-slate-100 p-4">
                <button
                  onClick={() => { setMobileOpen(false); openQuote(); }}
                  className="w-full rounded-full bg-brand-orange py-3 font-heading text-sm font-bold uppercase tracking-wide text-white"
                  data-testid="mobile-quote-btn"
                >
                  Get Free Quote
                </button>
                <div className="grid grid-cols-2 gap-2.5">
                  <a href={SITE.phone1} className="flex items-center justify-center gap-2 rounded-full border border-brand-blue/25 py-3 text-sm font-bold text-brand-blue" data-testid="mobile-call-btn">
                    <Phone className="h-4 w-4" /> Call
                  </a>
                  <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 text-sm font-bold text-white" data-testid="mobile-whatsapp-btn">
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
